import React from 'react';
import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { BaseStyles } from '@noths/polaris-client-ribbons-base';
import type { DeepPartial } from '@noths/polaris-dev-ts-recipes';
import type { AppInitialProps, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { ErrorBoundary } from '../Error/ErrorBoundary';
import { getLayout } from '../Layout/getLayout';
import { LayoutTypes } from '../Layout/types';
import type { NextConfig } from '../types/NextConfig';

const BrowserRum = dynamic(() =>
  import(/* webpackChunkName: "datadog_browser_rum" */ '../monitoring/BrowserRum').then(
    (mod) => mod.BrowserRum,
  ),
);

const BrowserLogs = dynamic(() =>
  import(/* webpackChunkName: "datadog_browser_logs" */ '../monitoring/BrowserLogs').then(
    (mod) => mod.BrowserLogs,
  ),
);

export interface BaseAppProps extends AppInitialProps {
  experiments?: string;
  featureFlags?: FeatureFlags;
  layoutType?: LayoutTypes;
  navData?: unknown;
  searchedTerm?: string;
  siteChromeCompactMode?: boolean;
  topBannerData?: unknown;
}

// Ensure the web app passes its own config from 'next/config' to this function.
// This is to maintain a singleton pattern so that only one instance of next.js is instantiated.
const BaseApp =
  <T extends DeepPartial<NextConfig>>(config: T) =>
  ({
    Component,
    featureFlags,
    pageProps,
    searchedTerm,
    siteChromeCompactMode = false,
    ...props
  }: AppProps & BaseAppProps) => {
    const {
      publicRuntimeConfig: {
        ENABLE_LOGGING,
        NODE_ENV,
        PROD_LIKE_ENV,
        SENTRY_KEY,
        SENTRY_PROJECT,
        env,
        monitoring,
        version,
      },
    } = config as Required<NextConfig>;
    const errorBoundaryProps = {
      env: NODE_ENV,
      isLoggingEnabled: ENABLE_LOGGING,
      sentry: {
        key: SENTRY_KEY,
        id: SENTRY_PROJECT,
      },
    };

    const defaultLayoutType = LayoutTypes.CLIENT_SITE_CHROME_UI;
    const navData = pageProps?.navData ?? props.navData;
    const topBannerData = pageProps?.topBannerData ?? props.topBannerData;
    const layoutType = pageProps?.layoutType ?? props.layoutType ?? defaultLayoutType;
    const LayoutComponent = getLayout(layoutType);
    const renderMonitoring = monitoring?.applicationId && env === 'production';

    return (
      <>
        <Head>
          <meta content="width=device-width, initial-scale=1" name="viewport" />
        </Head>
        {renderMonitoring && (
          <>
            <BrowserRum
              applicationId={monitoring?.applicationId}
              clientToken={monitoring?.clientToken}
              featureFlags={featureFlags}
              monitoringEnv={monitoring?.monitoringEnv}
              rumSampleRate={monitoring?.rumSampleRate}
              rumTracingURLs={monitoring?.rumTracingURLs}
              serviceName={monitoring?.serviceName}
              version={version}
            />
            <BrowserLogs
              clientToken={monitoring?.clientToken}
              forwardConsoleLogs={monitoring.forwardConsoleLogs}
              logsLoggerLevel={monitoring?.logsLoggerLevel}
              logsSampleRate={monitoring?.logsSampleRate}
              monitoringEnv={monitoring?.monitoringEnv}
              serviceName={monitoring?.serviceName}
              version={version}
            />
          </>
        )}
        <BaseStyles />
        <LayoutComponent
          NODE_ENV={NODE_ENV}
          featureFlags={featureFlags}
          isProdLikeEnv={PROD_LIKE_ENV}
          mergeBasket={pageProps?.mergeBasket}
          navData={navData}
          searchedTerm={searchedTerm}
          siteChromeCompactMode={siteChromeCompactMode}
          topBannerData={topBannerData}
        >
          <ErrorBoundary {...errorBoundaryProps}>
            <Component {...pageProps} />
          </ErrorBoundary>
        </LayoutComponent>
      </>
    );
  };

export { BaseApp };
